import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, MapPin, Search, UserPlus, X } from 'lucide-react';
import InvestorLogo from '../../../components/data/InvestorLogo';
import CountryFlag from '../../../components/countryFlags/countryFlag';
import { Dialog } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { cn } from '../../../lib/utils';
import {
  asString,
  getInvestorIndustries,
  getInvestorTypes,
} from '../../../components/investor-display/helpers';
import { useCRM } from '../../../hooks/crm';
import MultiSelectFilter from './CRMFilterDropdown';

const EMPTY_FILTERS = {
  countries: [],
  types: [],
  industries: [],
};

const normalizeValue = (value) => asString(value).trim();
const normalizeKey = (value) => normalizeValue(value).toLowerCase();

function buildFilterOptions(investors) {
  const countries = new Map();
  const types = new Map();
  const industries = new Map();

  investors.forEach((investor) => {
    const country = normalizeValue(investor?.country);
    if (country) {
      const key = normalizeKey(country);
      countries.set(key, {
        value: country,
        label: country,
        count: (countries.get(key)?.count || 0) + 1,
      });
    }

    getInvestorTypes(investor).forEach((type) => {
      const normalizedType = normalizeValue(type);
      if (!normalizedType) {
        return;
      }

      const key = normalizeKey(normalizedType);
      types.set(key, {
        value: normalizedType,
        label: normalizedType,
        count: (types.get(key)?.count || 0) + 1,
      });
    });

    getInvestorIndustries(investor).forEach((industry) => {
      const normalizedIndustry = normalizeValue(industry);
      if (!normalizedIndustry) {
        return;
      }

      const key = normalizeKey(normalizedIndustry);
      industries.set(key, {
        value: normalizedIndustry,
        label: normalizedIndustry,
        count: (industries.get(key)?.count || 0) + 1,
      });
    });
  });

  const sortOptions = (collection) =>
    [...collection.values()].sort((left, right) => left.label.localeCompare(right.label));

  return {
    countries: sortOptions(countries),
    types: sortOptions(types),
    industries: sortOptions(industries),
  };
}

function matchesSelection(selectedValues, candidateValues) {
  if (!selectedValues.length) {
    return true;
  }

  const normalizedCandidates = new Set(
    (candidateValues || []).map((value) => normalizeKey(value)).filter(Boolean)
  );

  return selectedValues.some((value) => normalizedCandidates.has(normalizeKey(value)));
}

function InvestorSelectionSkeleton() {
  return (
    <li className="rounded-md border border-stone-300 bg-white p-5 shadow-md shadow-blue-50">
      <div className="animate-pulse space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <div className="h-6 w-6 rounded bg-stone-200" />
            <div className="space-y-2">
              <div className="h-4 w-40 rounded bg-stone-200" />
              <div className="h-3 w-28 rounded bg-stone-200" />
            </div>
          </div>
          <div className="h-4 w-6 rounded bg-stone-200" />
        </div>
        <div className="flex gap-2">
          <div className="h-3 w-14 rounded bg-stone-200" />
          <div className="h-3 w-20 rounded bg-stone-200" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="h-3 w-24 rounded bg-stone-200" />
          <div className="h-7 w-24 rounded-full bg-stone-200" />
        </div>
      </div>
    </li>
  );
}

function InvestorSelectionCard({
  investor,
  isAdded,
  isAdding,
  wasAddedThisSession,
  onAdd,
  t,
}) {
  const country = useMemo(() => normalizeValue(investor?.country), [investor?.country]);
  const investorTypes = useMemo(() => getInvestorTypes(investor), [investor]);
  const nameText = asString(investor?.name).trim() || '—';

  return (
    <li
      className={cn(
        'flex h-full flex-col gap-4 rounded-md border border-stone-300 bg-white p-5 shadow-md shadow-blue-50 transition-colors',
        wasAddedThisSession ? 'border-emerald-200 bg-emerald-50/40' : 'hover:border-stone-400'
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <InvestorLogo
            id={investor?.id}
            country={investor?.country}
            className="h-6 w-6 flex-none"
            alt={`${nameText} logo`}
            width={24}
            height={24}
            loading="lazy"
            decoding="async"
          />

          <div className="min-w-0">
            <h3 className="break-words text-lg font-bold leading-tight text-stone-900">
              {nameText}
            </h3>
          </div>
        </div>

        <div className="flex h-6 w-8 flex-shrink-0 items-start justify-end overflow-hidden">
          {country ? (
            <CountryFlag
              countryName={country}
              width={24}
              height={16}
              loading="lazy"
              decoding="async"
            />
          ) : null}
        </div>
      </div>

      <div className="flex min-h-[1.25rem] flex-wrap gap-x-3 gap-y-1 text-sm text-stone-700">
        {investorTypes.length ? (
          investorTypes.map((typeItem, index) => (
            <span key={`${typeItem}-${index}`}>{typeItem}</span>
          ))
        ) : (
          <span className="italic text-stone-400">—</span>
        )}
      </div>

      <div className="mt-auto flex items-end justify-between gap-3">
        <div className="inline-flex min-w-0 items-center gap-2 text-xs text-stone-500">
          <MapPin className="h-3.5 w-3.5 flex-none text-stone-400" />
          <span className="truncate">{country || '—'}</span>
        </div>

        {isAdding ? (
          <Button
            variant="default"
            size="xs"
            wrap="nowrap"
            disabled
            iconLeft={<Loader2 className="animate-spin" />}
            className="min-w-[112px] !cursor-default rounded-full !bg-stone-100 !text-stone-950"
            aria-busy="true"
          >
            {t('app:crm.t68')}
          </Button>
        ) : isAdded ? (
          <Button
            variant="default"
            size="xs"
            wrap="nowrap"
            disabled
            iconLeft={<CheckCircle2 />}
            className="min-w-[112px] !cursor-default rounded-full !bg-stone-100 !text-stone-950"
          >
            {t('app:crm.t9')}
          </Button>
        ) : (
          <Button
            variant="default"
            size="xs"
            wrap="nowrap"
            iconLeft={<UserPlus />}
            onClick={() => onAdd(investor)}
            className="min-w-[112px] rounded-full !bg-stone-100 !text-stone-950 hover:!bg-stone-200"
          >
            {t('app:crm.t10')}
          </Button>
        )}
      </div>
    </li>
  );
}

const AddNewInvestorWindow = ({
  isOpen,
  onClose,
  boardId,
  columnId,
}) => {
  const { t } = useTranslation();
  const {
    boards,
    addInvestorToBoard,
    searchAvailableInvestors,
  } = useCRM();

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingInvestorIds, setPendingInvestorIds] = useState(() => new Set());
  const [sessionAddedInvestorIds, setSessionAddedInvestorIds] = useState(() => new Set());

  const selectedBoard = useMemo(
    () => boards.find((board) => String(board.board_id) === String(boardId)) || null,
    [boardId, boards]
  );

  const boardInvestorIds = useMemo(
    () => new Set(
      (selectedBoard?.columns || []).flatMap((column) =>
        (column?.investors || []).map((investor) => String(investor?.investor_id || ''))
      )
    ),
    [selectedBoard]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSearchQuery('');
    setFilters(EMPTY_FILTERS);
    setSessionAddedInvestorIds(new Set());
    setPendingInvestorIds(new Set());
  }, [boardId, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setLoading(true);

      try {
        const nextInvestors = await searchAvailableInvestors({
          boardId,
          query: searchQuery,
          excludeBoardInvestors: false,
        });

        if (!cancelled) {
          setInvestors(nextInvestors);
        }
      } catch (error) {
        console.error('Error loading investors:', error);

        if (!cancelled) {
          setInvestors([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, searchQuery.trim() ? 250 : 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [boardId, isOpen, searchAvailableInvestors, searchQuery]);

  const filterOptions = useMemo(() => buildFilterOptions(investors), [investors]);

  useEffect(() => {
    setFilters((previousFilters) => {
      const nextFilters = {
        countries: previousFilters.countries.filter((value) =>
          filterOptions.countries.some((option) => normalizeKey(option.value) === normalizeKey(value))
        ),
        types: previousFilters.types.filter((value) =>
          filterOptions.types.some((option) => normalizeKey(option.value) === normalizeKey(value))
        ),
        industries: previousFilters.industries.filter((value) =>
          filterOptions.industries.some((option) => normalizeKey(option.value) === normalizeKey(value))
        ),
      };

      if (
        nextFilters.countries.join('|') === previousFilters.countries.join('|')
        && nextFilters.types.join('|') === previousFilters.types.join('|')
        && nextFilters.industries.join('|') === previousFilters.industries.join('|')
      ) {
        return previousFilters;
      }

      return nextFilters;
    });
  }, [filterOptions]);

  const visibleInvestors = useMemo(
    () => investors.filter((investor) => {
      const countryMatches = matchesSelection(filters.countries, [investor?.country]);
      const typeMatches = matchesSelection(filters.types, getInvestorTypes(investor));
      const industryMatches = matchesSelection(filters.industries, getInvestorIndustries(investor));

      return countryMatches && typeMatches && industryMatches;
    }),
    [filters.countries, filters.industries, filters.types, investors]
  );

  const hasActiveFilters = Boolean(
    filters.countries.length
    || filters.types.length
    || filters.industries.length
  );

  const hasActiveView = Boolean(searchQuery.trim() || hasActiveFilters);

  const sessionAddedCount = sessionAddedInvestorIds.size;

  const handleToggleFilter = (key, value) => {
    setFilters((previousFilters) => {
      const currentValues = previousFilters[key] || [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((currentValue) => currentValue !== value)
        : [...currentValues, value];

      return {
        ...previousFilters,
        [key]: nextValues,
      };
    });
  };

  const handleResetView = () => {
    setSearchQuery('');
    setFilters(EMPTY_FILTERS);
  };

  const handleAddInvestor = async (investor) => {
    const investorId = String(investor?._id || '');

    if (!investorId || boardInvestorIds.has(investorId) || pendingInvestorIds.has(investorId)) {
      return;
    }

    setPendingInvestorIds((previousIds) => new Set(previousIds).add(investorId));

    try {
      const wasAdded = await addInvestorToBoard({
        boardId,
        columnId,
        investor,
      });

      if (wasAdded !== false) {
        setSessionAddedInvestorIds((previousIds) => new Set(previousIds).add(investorId));
      }
    } catch (error) {
      console.error('Error adding investor to column:', error);
    } finally {
      setPendingInvestorIds((previousIds) => {
        const nextIds = new Set(previousIds);
        nextIds.delete(investorId);
        return nextIds;
      });
    }
  };

  return (
    <Dialog
      size="4xl"
      isOpen={isOpen}
      onClose={onClose}
      title={t('app:crm.t7')}
      subtitle={selectedBoard?.board_name}
      stickyFooter
      closeOnOverlayClick
      overlayClassName="bg-white/10 backdrop-blur-[2px]"
      contentClassName="rounded-2xl border border-stone-200 shadow-2xl"
      bodyClassName="space-y-4"
      footerClassName="bg-white/90 backdrop-blur-[6px]"
      footerActions={(
        <div className="flex flex-wrap items-center justify-end gap-2">
          {sessionAddedCount > 0 ? (
            <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-stone-600">
              {t('app:crm.t67', { count: sessionAddedCount })}
            </span>
          ) : null}

          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="rounded-full border-stone-200 bg-white px-4 text-xs text-stone-700 hover:bg-stone-50"
          >
            {t('app:crm.t12')}
          </Button>
        </div>
      )}
    >
      <div className="sticky top-[-1.5rem] z-10 -mx-6 -mt-6 border-b border-stone-200 bg-white px-6 pb-4 pt-7 shadow-sm">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
            <div className="relative min-w-0 flex-1">
              <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">
                <Search className="h-4 w-4" />
              </div>

              <Input
                multi={false}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t('app:crm.t8')}
                className="w-full border-stone-200 bg-white pl-8 shadow-none"
              />

              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center justify-center text-stone-400 transition-colors hover:text-stone-700"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <MultiSelectFilter
                label={t('app:crm.t54')}
                selectedValues={filters.countries}
                options={filterOptions.countries}
                showCountryFlags
                onToggle={(value) => handleToggleFilter('countries', value)}
                emptyLabel={t('app:crm.t65')}
              />

              <MultiSelectFilter
                label={t('app:crm.t55')}
                selectedValues={filters.types}
                options={filterOptions.types}
                onToggle={(value) => handleToggleFilter('types', value)}
                emptyLabel={t('app:crm.t65')}
              />

              <MultiSelectFilter
                label={t('app:crm.t56')}
                selectedValues={filters.industries}
                options={filterOptions.industries}
                onToggle={(value) => handleToggleFilter('industries', value)}
                emptyLabel={t('app:crm.t65')}
              />

              {hasActiveView ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetView}
                  className="rounded-full px-3 text-xs text-stone-600 hover:text-stone-900"
                >
                  {t('app:crm.t58')}
                </Button>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-stone-500">
            <span>{t('app:crm.t53', { visible: visibleInvestors.length, total: investors.length })}</span>

            {loading ? (
              <span className="inline-flex items-center gap-1 text-stone-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t('app:crm.t15')}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {loading ? (
        <ul role="list" className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <InvestorSelectionSkeleton key={`investor-selection-skeleton-${index}`} />
          ))}
        </ul>
      ) : visibleInvestors.length ? (
        <ul role="list" className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {visibleInvestors.map((investor) => {
            const investorId = String(investor?._id || '');

            return (
              <InvestorSelectionCard
                key={investorId}
                investor={investor}
                isAdded={boardInvestorIds.has(investorId)}
                isAdding={pendingInvestorIds.has(investorId)}
                wasAddedThisSession={sessionAddedInvestorIds.has(investorId)}
                onAdd={handleAddInvestor}
                t={t}
              />
            );
          })}
        </ul>
      ) : (
        <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/60 px-6 py-10 text-center text-sm text-stone-500">
          {hasActiveFilters ? t('app:crm.t66') : t('app:crm.t11')}
        </div>
      )}
    </Dialog>
  );
};

export default AddNewInvestorWindow;
